/**
 * PercentileRails — Savant-style league percentile ranking bars.
 *
 * Fetches /api/v2/stats/percentiles/:team/:player/:year and renders
 * one rail per stat: label · gradient-positioned bar · percentile bubble · value.
 * Blue = cold (low percentile), red = hot (high percentile).
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Skeleton, Chip, useTheme, alpha, Tooltip,
} from '@mui/material';
import { statsApi } from '../../services/apiService';

// Interpolate Savant-ish color: 0 → blue, 50 → gray, 100 → red
export const percentileColor = (pct) => {
  const lerp = (a, b, t) => Math.round(a + (b - a) * t);
  const blue = [33, 113, 181];
  const gray = [130, 138, 146];
  const red = [214, 39, 40];
  const t = Math.max(0, Math.min(100, pct)) / 100;
  const [c1, c2, tt] = t < 0.5 ? [blue, gray, t * 2] : [gray, red, (t - 0.5) * 2];
  return `rgb(${lerp(c1[0], c2[0], tt)}, ${lerp(c1[1], c2[1], tt)}, ${lerp(c1[2], c2[2], tt)})`;
};

const Rail = ({ row }) => {
  const theme = useTheme();
  const color = percentileColor(row.percentile);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.45 }}>
      <Typography sx={{ width: 64, flexShrink: 0, fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
        {row.label}
      </Typography>
      <Box sx={{ flex: 1, position: 'relative', height: 22, display: 'flex', alignItems: 'center' }}>
        {/* Track */}
        <Box sx={{ position: 'absolute', left: 0, right: 0, height: 5, borderRadius: 2.5, bgcolor: alpha(theme.palette.text.primary, 0.08) }} />
        {/* Midline (league average) */}
        <Box sx={{ position: 'absolute', left: '50%', top: 2, bottom: 2, width: '1px', bgcolor: alpha(theme.palette.text.primary, 0.18) }} />
        {/* Fill */}
        <Box sx={{ position: 'absolute', left: 0, width: `${row.percentile}%`, height: 5, borderRadius: 2.5, bgcolor: alpha(color, 0.55), transition: 'width 0.5s ease' }} />
        {/* Bubble */}
        <Tooltip title={`${row.percentile}th percentile${row.lowerBetter ? ' (lower is better)' : ''}`} arrow>
          <Box sx={{
            position: 'absolute', left: `calc(${row.percentile}% - 11px)`,
            width: 23, height: 23, borderRadius: '50%', bgcolor: color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `2px solid ${theme.palette.background.paper}`,
            boxShadow: `0 1px 4px ${alpha('#000', 0.35)}`,
            transition: 'left 0.5s ease', zIndex: 1, cursor: 'default',
          }}>
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#fff', lineHeight: 1 }}>
              {row.percentile}
            </Typography>
          </Box>
        </Tooltip>
      </Box>
      <Typography sx={{ width: 52, flexShrink: 0, textAlign: 'right', fontSize: '0.78rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
        {row.display}
      </Typography>
    </Box>
  );
};

const PercentileRails = ({ team, playerName, year, playerType }) => {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!team || !playerName || !year) return;
    let cancelled = false;
    setLoading(true);
    statsApi.getPercentiles(team, playerName.replace(/\s+/g, '_'), year)
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [team, playerName, year]);

  if (loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Skeleton width={220} height={22} sx={{ mb: 1.5 }} />
          {[...Array(6)].map((_, i) => <Skeleton key={i} height={24} sx={{ my: 0.5 }} />)}
        </CardContent>
      </Card>
    );
  }

  if (!data || (!data.batting?.length && !data.pitching?.length)) return null;

  const sections = [];
  if (playerType !== 'pitcher' && data.batting?.length) sections.push({ title: 'Hitting', rows: data.batting, qualified: data.qualified?.batting });
  if (playerType !== 'batter' && data.pitching?.length) sections.push({ title: 'Pitching', rows: data.pitching, qualified: data.qualified?.pitching });
  if (sections.length === 0) {
    // e.g. two-way edge cases — show whatever came back
    if (data.batting?.length) sections.push({ title: 'Hitting', rows: data.batting, qualified: data.qualified?.batting });
    if (data.pitching?.length) sections.push({ title: 'Pitching', rows: data.pitching, qualified: data.qualified?.pitching });
  }
  if (sections.length === 0) return null;

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            League Percentile Rankings
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {data.mode === 'rolling' && (
              <Chip label="30-day CVR" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />
            )}
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
              vs {Math.max(data.poolSize?.batting || 0, data.poolSize?.pitching || 0)} qualified players
            </Typography>
          </Box>
        </Box>
        {sections.map((sec, si) => (
          <Box key={sec.title} sx={{ mt: si > 0 ? 1.5 : 0 }}>
            {sections.length > 1 && (
              <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.68rem', color: 'text.secondary', textTransform: 'uppercase' }}>
                {sec.title}
              </Typography>
            )}
            {!sec.qualified && (
              <Typography variant="caption" sx={{ display: 'block', color: alpha(theme.palette.warning.main, 0.9), fontSize: '0.65rem', mb: 0.25 }}>
                Below qualification threshold — percentiles shown vs qualified pool
              </Typography>
            )}
            {sec.rows.map(row => <Rail key={row.stat} row={row} />)}
          </Box>
        ))}
        {/* Legend */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25, justifyContent: 'center' }}>
          <Typography variant="caption" sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>Poor</Typography>
          <Box sx={{ width: 120, height: 5, borderRadius: 2.5, background: `linear-gradient(90deg, ${percentileColor(0)}, ${percentileColor(50)}, ${percentileColor(100)})` }} />
          <Typography variant="caption" sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>Elite</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PercentileRails;
